import React, { useState, useEffect, useRef } from 'react';
import { doc, updateDoc, collection, addDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig'; // Aapka correct path
import { X, Send, Bot, User, ShieldAlert, MessageSquare, Loader2, CalendarDays, Image as ImageIcon } from 'lucide-react';

const ChatDrawer = ({ isOpen, onClose, chatData, clientId }) => {
  const [newMessage, setNewMessage] = useState('');
  const [isAiPaused, setIsAiPaused] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  
  // Auto-scroll logic
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, isOpen]);

  useEffect(() => {
    if (!isOpen || !chatData) return;

    setIsAiPaused(chatData.botPaused || false);
    setIsLoadingChats(true);
    
    const cleanPhoneId = String(chatData.id).replace(/\D/g, '');
    const currentClientId = clientId || chatData.clientId;

    let activeUnsubscribe = null;

    const setupListener = (refPath) => {
        activeUnsubscribe = onSnapshot(refPath, (snapshot) => {
            let fetchedMessages = [];
            snapshot.forEach(doc => fetchedMessages.push({ id: doc.id, ...doc.data() }));

            const getTime = (msg) => {
                if (msg.time?.toMillis) return msg.time.toMillis();
                if (msg.timestamp?.toMillis) return msg.timestamp.toMillis();
                if (msg.time) return new Date(msg.time).getTime();
                if (msg.timestamp) return new Date(msg.timestamp).getTime();
                return 0; 
            };

            fetchedMessages.sort((a, b) => getTime(a) - getTime(b));

            if (fetchedMessages.length === 0) {
                const fallbackMsg = chatData.message || chatData.text || chatData.query || chatData.lastAction;
                if (fallbackMsg) {
                    setChatHistory([{ role: 'user', content: fallbackMsg, time: new Date() }]);
                } else {
                    setChatHistory([]);
                }
            } else {
                setChatHistory(fetchedMessages);
            }
            setIsLoadingChats(false);
        });
    };

    const checkAndListen = async () => {
        if (currentClientId && currentClientId !== "Root DB") {
            const clientMsgsRef = collection(db, "clients", currentClientId, "chats", cleanPhoneId, "messages");
            setupListener(clientMsgsRef); 
            return;
        }
        
        const rootMsgsRef = collection(db, "users", cleanPhoneId, "messages");
        setupListener(rootMsgsRef);
    };

    checkAndListen();

    return () => {
        if (activeUnsubscribe) activeUnsubscribe();
    };

  }, [isOpen, chatData, clientId]);

  if (!isOpen || !chatData) return null;

  const handleToggleAi = async () => {
    const newStatus = !isAiPaused;
    setIsAiPaused(newStatus);
    try {
      // Chat data mein mode hua toh wo Root user hai warna client ki lead hai
      const targetCollection = chatData.mode ? "users" : "leads"; 
      
      const currentClientId = clientId || chatData.clientId;
      if(currentClientId && currentClientId !== "Root DB") {
         // Client Database Update (CRM)
         await updateDoc(doc(db, "clients", currentClientId, "chats", chatData.id), { botPaused: newStatus });
      } else {
         // Root Database Update
         await updateDoc(doc(db, targetCollection, chatData.id), { botPaused: newStatus });
      }
    } catch (error) { console.error("Failed to pause AI:", error); }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    // Agent message structure
    const messagePayload = { role: 'agent', content: newMessage, time: new Date() };

    try {
      setNewMessage('');
      
      // Auto pause AI if human sends message
      setIsAiPaused(true);
      
      const cleanPhoneId = String(chatData.id).replace(/\D/g, '');
      const currentClientId = clientId || chatData.clientId;

      if (currentClientId && currentClientId !== "Root DB") {
          // Send to Client CRM
          await updateDoc(doc(db, "clients", currentClientId, "chats", chatData.id), { botPaused: true });
          const msgsRef = collection(db, "clients", currentClientId, "chats", cleanPhoneId, "messages");
          await addDoc(msgsRef, messagePayload);
      } else {
          // Send to Root DB
          const targetCollection = chatData.mode ? "users" : "leads"; 
          await updateDoc(doc(db, targetCollection, chatData.id), { botPaused: true });
          const msgsRef = collection(db, "users", cleanPhoneId, "messages");
          await addDoc(msgsRef, messagePayload);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const formatTime = (timeData) => {
    if (!timeData) return '';
    const date = timeData.toDate ? timeData.toDate() : new Date(timeData);
    const today = new Date();
    const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
    
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Today, ${timeStr}`;
    return `${date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}, ${timeStr}`;
  };

  const studentName = chatData.whatsappName || chatData.studentName || chatData.name || chatData.phone || 'New Lead';
  const initial = studentName.charAt(0).toUpperCase();

  // 🔥 THE FIX: Message Renderer Logic (Blast + Templates + Images + Text)
  const renderMessageContent = (msg) => {
    // 1. Agar message mein direct image link hai (Media msg)
    if (msg.imageUrl || msg.mediaUrl) {
      return (
        <div className="flex flex-col gap-2">
            <div className="relative rounded-lg overflow-hidden border border-black/10 shadow-sm bg-black/5 flex items-center justify-center min-h-[100px]">
                <img 
                    src={msg.imageUrl || msg.mediaUrl} 
                    alt="Chat Media" 
                    className="max-w-full max-h-[250px] object-cover" 
                    onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                />
                <div className="hidden absolute inset-0 items-center justify-center text-slate-400">
                    <ImageIcon size={24} />
                </div>
            </div>
            {msg.content && msg.content !== "[Media/Interactive Option Sent]" && (
               <p className="mt-1">{msg.content}</p>
            )}
        </div>
      );
    }

    // 2. Agar message interactive / button type ka hai (Blast Campaigns)
    if (msg.type === "interactive" || msg.buttons || msg.content === "[Media/Interactive Option Sent]") {
        const textContent = msg.text?.body || msg.content?.replace("[Media/Interactive Option Sent]", "") || "Template Broadcast Sent";
        
        return (
            <div className="flex flex-col gap-2">
                <p className="whitespace-pre-wrap">{textContent}</p>
                
                {/* Agar DB mein buttons array hai toh render karo, warna default dikhao */}
                <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-black/10">
                    {msg.buttons && msg.buttons.length > 0 ? (
                        msg.buttons.map((btn, i) => (
                            <span key={i} className="bg-black/10 text-current text-[11px] font-bold px-2.5 py-1.5 rounded-md shadow-sm">
                                {btn.title || btn}
                            </span>
                        ))
                    ) : (
                        <span className="bg-black/10 text-current text-[11px] font-bold px-2.5 py-1.5 rounded-md shadow-sm border border-black/5 opacity-70">
                            🔘 Interactive Buttons
                        </span>
                    )}
                </div>
            </div>
        );
    }

    // 3. Normal Text Message
    return <p className="whitespace-pre-wrap">{msg.content || msg.text || msg.message}</p>;
  };

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity" onClick={onClose}></div>
      
      <div className="fixed top-0 right-0 h-[100dvh] w-full sm:max-w-[450px] bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200">
        
        <div className="h-16 md:h-20 px-4 md:px-6 border-b border-slate-200 flex items-center justify-between bg-white shrink-0 z-10 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold shadow-sm border border-indigo-200">
              {initial}
            </div>
            <div>
              <h3 className="text-sm md:text-base font-bold text-slate-900 tracking-tight line-clamp-1">{studentName}</h3>
              <p className="text-[11px] font-semibold text-slate-500 mt-0.5 flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${isAiPaused ? 'bg-rose-500' : 'bg-emerald-500'}`}></span>
                +{chatData.id}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <div 
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-full border shadow-sm cursor-pointer transition-colors ${isAiPaused ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`} 
              onClick={handleToggleAi}
              title="Toggle AI Bot"
            >
              <span className={`text-[10px] font-bold uppercase tracking-wide ${isAiPaused ? 'text-rose-600' : 'text-emerald-600'}`}>
                {isAiPaused ? 'Human' : 'Bot'}
              </span>
              <button className={`w-8 h-4 rounded-full relative transition-colors ${isAiPaused ? 'bg-rose-500' : 'bg-emerald-500'}`}>
                <div className={`w-3 h-3 bg-white rounded-full absolute top-[2px] transition-all shadow-sm ${isAiPaused ? 'left-4' : 'left-[2px]'}`}></div>
              </button>
            </div>
            
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-all">
              <X size={20} />
            </button>
          </div>
        </div>

        {isAiPaused && (
          <div className="bg-rose-500 text-white p-1.5 flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-wider shrink-0 shadow-inner">
            <ShieldAlert size={14} /> AI is currently paused. You have control.
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 bg-[#F0F2F5] custom-scrollbar">
           {isLoadingChats ? (
             <div className="flex flex-col items-center justify-center h-full text-slate-400 space-y-3">
                <Loader2 size={28} className="animate-spin text-indigo-500" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Syncing Messages...</p>
             </div>
           ) : chatHistory.length > 0 ? (
             <div className="space-y-4">
                <div className="flex justify-center my-4">
                   <span className="bg-white/80 backdrop-blur-sm text-slate-500 text-[10px] font-bold uppercase px-3 py-1 rounded-full shadow-sm flex items-center gap-1.5">
                     <CalendarDays size={12}/> Chat History
                   </span>
                </div>

                {chatHistory.map((msg, index) => {
                  const isUser = msg.role === 'user' || msg.sender === 'user'; 
                  const isAgent = msg.role === 'agent' || msg.sender === 'agent';
                  
                  return (
                    <div key={index} className={`flex ${isUser ? 'justify-start' : 'justify-end'} w-full`}>
                      <div className={`flex gap-2 max-w-[85%] ${!isUser && 'flex-row-reverse'}`}>
                        
                        <div className="mt-auto shrink-0">
                          {isUser ? (
                             <div className="w-6 h-6 rounded-full bg-slate-300 flex items-center justify-center text-white text-[10px] font-bold shadow-sm">{initial}</div>
                          ) : (
                             <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white shadow-sm ${isAgent ? 'bg-indigo-900' : 'bg-emerald-500'}`}>
                               {isAgent ? <User size={12}/> : <Bot size={12}/>}
                             </div>
                          )}
                        </div>

                        <div className={`flex flex-col ${isUser ? 'items-start' : 'items-end'}`}>
                          <div className={`p-3 md:p-3.5 shadow-sm relative ${
                            isUser 
                              ? 'bg-white text-slate-800 rounded-2xl rounded-bl-none border border-slate-100' 
                              : 'bg-indigo-600 text-white rounded-2xl rounded-br-none'
                            }`}>
                            
                            <div className="text-[13px] md:text-sm font-medium leading-relaxed">
                                {renderMessageContent(msg)}
                            </div>   
                            
                            <div className={`text-[9px] font-bold mt-1.5 flex items-center justify-end gap-1 ${isUser ? 'text-slate-400' : 'text-indigo-200'}`}>
                               {formatTime(msg.time)}
                            </div>
                          </div>
                          
                          <span className={`text-[9px] font-bold text-slate-400 mt-1 ${isUser ? 'ml-1' : 'mr-1'}`}>
                            {isUser ? 'Customer' : (isAgent ? 'Human Agent' : 'AI Assistant')}
                          </span>
                        </div>
                     </div>
                   </div>
                 );
               })}
               <div ref={messagesEndRef} className="h-4" />
             </div>
           ) : (
             <div className="flex flex-col items-center justify-center h-full text-slate-400 space-y-3">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm">
                  <MessageSquare size={28} className="text-slate-300" />
                </div>
                <p className="text-xs font-bold text-slate-500">No chat history yet</p>
                <p className="text-[10px] text-slate-400">Send a message to start the conversation.</p>   
             </div>
           )}
        </div>

        <div className="p-3 md:p-4 bg-white border-t border-slate-200 shrink-0 pb-6 md:pb-4 relative z-20">
          <form onSubmit={handleSendMessage} className="flex items-center gap-3">
            <div className="relative flex-1">
              <input 
                type="text" 
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your reply here..." 
                className="w-full bg-slate-50 border border-slate-200 rounded-full px-5 py-3 text-sm font-medium text-slate-700 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:bg-white transition-all shadow-inner placeholder:text-slate-400"
              />
            </div>
            <button 
              type="submit" 
              disabled={!newMessage.trim()} 
              className="p-3 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 hover:shadow-lg disabled:opacity-50 disabled:hover:shadow-none disabled:bg-slate-300 transition-all shrink-0 shadow-md"
            >
              <Send size={18} className="ml-0.5" />
            </button>
          </form>
          <div className="flex justify-center mt-3">
            <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-3 py-1 rounded-full border border-slate-100">
              <Bot size={10} className="inline mr-1 mb-0.5 text-indigo-400"/> Replying will automatically pause AI
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatDrawer;